import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { Sparkles, ChessQueen } from 'lucide-react';
import { Player } from "@lottiefiles/react-lottie-player";
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { services } from "./data";

gsap.registerPlugin(ScrollTrigger);

const ServicesSection = () => {

    useEffect(() => {
        // Animation des cartes de services optimisée
        gsap.fromTo(".service-card",
            { y: 30, opacity: 0 },
            {
                y: 0,
                opacity: 1,
                duration: 0.5,
                stagger: 0.08,
                ease: "power3.out",
                scrollTrigger: {
                    trigger: ".services-section",
                    start: "top 85%",
                    end: "bottom 20%",
                    toggleActions: "play none none reverse"
                }
            }
        );
    }, []);

    return (
        <section className="py-24 relative overflow-hidden services-section">

            {/* BACKGROUND CIRCLES - Optimisés */}
            <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
                {/* Large Circle - Deep Blue */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 0.1, scale: 1 }}
                    transition={{ duration: 2, ease: "easeOut" }}
                    className="absolute -top-1/4 -left-1/4 w-[1000px] h-[1000px] rounded-full bg-blue-900/30 blur-3xl"
                />

                {/* Giant Circle - Black/Blue */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 0.1, scale: 1 }}
                    transition={{ duration: 2, delay: 0.2, ease: "easeOut" }}
                    className="absolute top-1/4 -right-1/4 w-[1200px] h-[1200px] rounded-full bg-blue-950/30 blur-3xl"
                />

                {/* Particles for Texture */}
                <div className="absolute inset-0">
                    {[...Array(15)].map((_, i) => (
                        <motion.div
                            key={i}
                            animate={{
                                y: [0, -80, 0],
                                opacity: [0.1, 0.3, 0.1],
                            }}
                            transition={{
                                duration: 8 + i,
                                repeat: Infinity,
                                delay: i * 0.3,
                                ease: "linear"
                            }}
                            className="absolute w-20 h-20 rounded-full blur-xl bg-blue-500/5"
                            style={{
                                left: `${Math.random() * 100}%`,
                                top: `${Math.random() * 100}%`,
                            }}
                        />
                    ))}
                </div>
            </div>

            <div className="container mx-auto px-4 relative z-10">

                {/* En-tête de section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <div className="inline-flex items-center gap-2 px-5 py-2 bg-blue-500/10 rounded-full mb-6 border border-blue-500/20 backdrop-blur-sm">
                        <ChessQueen className="w-4 h-4 text-blue-400" />
                        <span className="text-blue-400 text-sm font-medium tracking-wide">EXCELLENCE DIGITALE</span>
                    </div>

                    <h2 className="text-4xl sm:text-5xl md:text-7xl font-bold text-white mb-6 md:mb-8 leading-tight tracking-tight">
                        <span className="text-white">Nos</span>
                        <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-blue-600 bg-clip-text text-transparent px-2 sm:px-4">Services</span>
                    </h2>

                    <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed">
                        Des solutions technologiques de pointe conçues pour accélérer votre croissance et transformer votre vision en réalité.
                    </p>

                </motion.div>

                {/* Grille des services - Responsive amélioré */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">

                    {services.map((service, idx) => (

                        <motion.div
                            key={service.id}
                            className="group relative service-card h-full"
                            whileHover={{ y: -8 }}
                            transition={{ duration: 0.3, ease: "easeOut" }}
                        >

                            {/* Glow Effect au survol */}
                            <div className={`absolute -inset-0.5 bg-gradient-to-r ${service.color || "from-blue-500 to-cyan-500"} rounded-3xl opacity-0 group-hover:opacity-30 blur-lg transitionDuration-300 transition-opacity`} />

                            {/* Carte de service */}
                            <div className="relative bg-gray-900/60 backdrop-blur-xl rounded-3xl p-6 md:p-8 border border-gray-800 group-hover:border-blue-500/30 transition-colors duration-300 h-full flex flex-col md:flex-row gap-6 items-center md:items-start text-center md:text-left">

                                {/* Animation Lottie */}
                                <div className="shrink-0 w-32 h-32 md:w-40 md:h-40 flex items-center justify-center bg-blue-500/5 rounded-2xl p-2 group-hover:bg-blue-500/10 transition-colors">
                                    <Player
                                        autoplay
                                        loop
                                        src={service.animation}
                                        className="w-full h-full"
                                    />
                                </div>

                                {/* Contenu texte */}
                                <div className="flex-1 min-w-0">

                                    <div className="flex items-center gap-3 mb-3 justify-center md:justify-start">
                                        <span className="text-blue-500/80 text-sm font-mono font-bold">0{service.id}</span>
                                        <div className="h-px w-12 bg-blue-500/20" />
                                    </div>

                                    <h3 className="text-xl md:text-2xl font-bold text-white mb-3 group-hover:text-blue-300 transition-colors">
                                        {service.title}
                                    </h3>

                                    <p className="text-gray-400 text-sm md:text-base leading-relaxed mb-4 line-clamp-3">
                                        {service.text}
                                    </p>

                                    <div className="flex items-center gap-2 text-cyan-400 text-sm font-medium opacity-0 group-hover:opacity-100 transform translate-x-[-10px] group-hover:translate-x-0 transition-all duration-300 justify-center md:justify-start">
                                        <span>Découvrir</span>
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
                                    </div>

                                </div>

                            </div>
                        </motion.div>

                    ))}

                </div>

            </div>

        </section>
    );
};

export default ServicesSection;
